import type { ReactNode } from "react";
import { LoadingScreen } from "@/components/loading-screen";
import { useI18n } from "@/locales/i18n-provider";
import { EmbeddedNotice } from "./components/embedded-notice";
import { useEmbeddedHost } from "./embedded-host-provider";
import { useHasEmbeddedAccessToken } from "./hooks/use-embedded-access-token";

/**
 * 嵌入端的认证边界（设计 §8.3、§13）。
 *
 * 它代替独立端的 `AuthenticationBoundary`：等宿主通过 MessageChannel 交付 Token，
 * 而不是发起 OIDC 登录。**任何分支都不做顶层导航**，也永远不加载 IAM 页面（AR-27）。
 */
export function EmbeddedTokenBoundary({ children }: { children: ReactNode }) {
  const { t } = useI18n();
  const { connection } = useEmbeddedHost();
  const hasToken = useHasEmbeddedAccessToken();

  switch (connection.status) {
    case "invalid-config":
      return <EmbeddedNotice title={t("embedded.invalidConfig.title")} detail={t("embedded.invalidConfig.detail")} />;
    case "not-embedded":
      return <EmbeddedNotice title={t("embedded.notEmbedded.title")} detail={t("embedded.notEmbedded.detail")} />;
    case "handshake-timeout":
      return <EmbeddedNotice title={t("embedded.handshakeFailed.title")} detail={t("embedded.handshakeFailed.detail")} />;
    case "session-ended":
      return (
        <EmbeddedNotice
          title={t("embedded.sessionEnded.title")}
          detail={connection.reason === "unauthorized" ? t("embedded.sessionEnded.unauthorized") : t("embedded.sessionEnded.detail")}
        />
      );
    case "connecting":
      return <LoadingScreen detail={t("embedded.connecting.detail")} title={t("embedded.connecting.title")} variant="inline" />;
    default:
      break;
  }

  if (!hasToken) {
    return <LoadingScreen detail={t("embedded.waitingToken.detail")} title={t("embedded.waitingToken.title")} variant="inline" />;
  }

  return <>{children}</>;
}
